import { Modal, Stack, Text, Code, Badge, Group } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Locale } from "@iso27001/shared";
import { formatDateTime } from "../i18n/format-date.js";
import { useEnumLabel } from "../i18n/enum-label.js";

export interface AuditEntry {
  id: number;
  entity: string;
  entityId: number | null;
  op: "create" | "update" | "delete";
  userId: number | null;
  at: number;
  beforeJson: string | null;
  afterJson: string | null;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Stable key (e.g. ['bedrohungen', 'audit', id]) for React Query caching. */
  queryKey: readonly unknown[];
  /** Called only while opened === true. Should return { items: AuditEntry[] }. */
  fetchFn: () => Promise<{ items: AuditEntry[] }>;
}

const opColor = { create: "blue", update: "yellow", delete: "red" } as const;

export function AuditHistoryModal({ opened, onClose, queryKey, fetchFn }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const { label } = useEnumLabel();
  const { data } = useQuery({
    queryKey,
    queryFn: fetchFn,
    enabled: opened,
  });
  return (
    <Modal opened={opened} onClose={onClose} title={t("common.action.history")} size="lg">
      <Stack>
        {!data && <Text c="dimmed">{t("audit.list.loading")}</Text>}
        {data && data.items.length === 0 && <Text c="dimmed">{t("audit.list.empty")}</Text>}
        {data && data.items.map((e) => (
          <Stack key={e.id} gap={4}>
            <Group>
              <Badge color={opColor[e.op]}>{label("auditOp", e.op)}</Badge>
              <Text size="xs" c="dimmed">{formatDateTime(e.at, locale)}</Text>
              {e.userId && <Text size="xs" c="dimmed">{t("audit.history.userFallback", { id: e.userId })}</Text>}
            </Group>
            {e.beforeJson && <Code block>{`${t("audit.history.before")} ${e.beforeJson}`}</Code>}
            {e.afterJson && <Code block>{`${t("audit.history.after")} ${e.afterJson}`}</Code>}
          </Stack>
        ))}
      </Stack>
    </Modal>
  );
}
